import { useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { processImage } from "../../lib/imageProcess";
import { enqueueLocal, syncEntry } from "../../lib/photoQueue";
import { API_BASE } from "../../lib/api";
import { rpc } from "../../lib/rpc";

/**
 * PhotoFilePicker -- "Hochladen vom PC" für Admin-Formulare (#114).
 *
 * Kein Kamera-Dialog: ein Datei-Feld, Mehrfachauswahl, Vorschau-Kacheln.
 * Die Dateien werden clientseitig verkleinert (processImage) und vom
 * Formular nach dem Speichern per uploadPendingPhotos() hochgeladen --
 * beim Anlegen existiert die Artikel-ID erst nach dem POST.
 *
 * Props:
 *   value    -- Array pending [{ key, url, processed, name }]
 *   onChange -- (nextValue) => void
 */
export function PhotoFilePicker({ value = [], onChange, testId = "photo-file-picker" }) {
    const inputRef = useRef(null);
    const [processing, setProcessing] = useState(false);

    const handleFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (files.length === 0) return;
        setProcessing(true);
        const next = [...value];
        for (const file of files) {
            try {
                const processed = await processImage(file);
                next.push({
                    key: `${file.name}-${file.size}-${Date.now()}-${next.length}`,
                    name: file.name,
                    url: URL.createObjectURL(processed.blob),
                    processed,
                });
            } catch (err) {
                toast.error(`${file.name}: ${err.message}`);
            }
        }
        setProcessing(false);
        onChange?.(next);
    };

    const remove = (key) => {
        const it = value.find((v) => v.key === key);
        if (it?.url) URL.revokeObjectURL(it.url);
        onChange?.(value.filter((v) => v.key !== key));
    };

    return (
        <div className="flex flex-col gap-2" data-testid={testId}>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFiles}
                className="hidden"
                data-testid={`${testId}-input`}
            />
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => inputRef.current?.click()}
                disabled={processing}
                data-testid={`${testId}-button`}
            >
                {processing ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                    <Upload className="mr-1 h-4 w-4" />
                )}
                Hochladen vom PC
            </Button>
            {value.length > 0 ? (
                <ul className="flex flex-wrap gap-2" data-testid={`${testId}-list`}>
                    {value.map((it) => (
                        <li
                            key={it.key}
                            className="relative h-16 w-16 overflow-hidden rounded-md border border-border"
                            data-testid={`${testId}-item`}
                        >
                            <img src={it.url} alt={it.name} className="h-full w-full object-cover" />
                            <button
                                type="button"
                                onClick={() => remove(it.key)}
                                aria-label={`${it.name} entfernen`}
                                className="absolute right-0 top-0 rounded-bl-md bg-background/80 p-0.5 text-foreground"
                                data-testid={`${testId}-remove`}
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

/**
 * Lädt die gewählten Dateien als Artikelbilder hoch und bindet sie an die
 * Entität (Upload ist zweistufig: POST /photos/upload, dann PATCH-Bindung wie
 * in den Mobile-Flows). Gibt die Anzahl erfolgreicher Uploads zurück; Fehler
 * werden je Datei als Toast gemeldet.
 */
export async function uploadPendingPhotos(pending, { entityType, entityId, purpose = "article_image" }) {
    let ok = 0;
    for (const it of pending) {
        try {
            const entry = await enqueueLocal({
                entityType,
                entityId,
                purpose,
                isEvidence: false,
                blob: it.processed.blob,
                thumb: it.processed.thumb,
                width: it.processed.width,
                height: it.processed.height,
                mime: it.processed.mime,
                ext: it.processed.ext,
                takenAt: new Date().toISOString(),
            });
            const resp = await syncEntry(entry, API_BASE);
            const bound = await rpc.patch(`/photos/${resp.id}`, {
                entity_type: entityType,
                entity_id: entityId,
            });
            if (!bound.ok) throw new Error(bound.error || "Bindung fehlgeschlagen");
            ok += 1;
        } catch (e) {
            toast.error(`Foto ${it.name}: ${e.message}`);
        }
        if (it.url) URL.revokeObjectURL(it.url);
    }
    return ok;
}
